// Cache identities for the `./ehr` door, moved here at ARCFE-1087 with the five
// hooks that were free of the app tethers (store / toaster / intl / router /
// app-alias) once D1 severed transport.
//
// THE STRING LITERALS ARE FROZEN AND ARE NOT NEW. Each one is transcribed from
// the hook file that declared it, and `queryKeys.freeze.test.ts` pins the whole
// TUPLE — not just the head — because a key is a cache identity: change a
// literal and every in-flight entry is orphaned while every prefix invalidator
// silently stops matching.
//
// THE SPELLINGS ARE INCONSISTENT ON PURPOSE. `GET_SYNC_NOTE_OPTIONS` is
// `'GetNoteOptionsForIntegration'` (constant name and value disagree),
// `GET_CLINIKO_INFO` is lowerCamel `'getClinikoPractitionerInfo'` where its
// neighbours are UpperCamel, and the note-options tuple takes a BARE
// `sessionId` while every other key here wraps its argument in an object.
// Normalising any of that would be a cache flush, so it is recorded instead.
//
// Two of these were module-PRIVATE in the app hooks. They are exported here
// because a `/contracts` key factory is the sanctioned single declaration site;
// the app paths re-export them so nothing that already imported one breaks.

/** `GET /integrations/session/{id}/note-options`. */
export const GET_SYNC_NOTE_OPTIONS = 'GetNoteOptionsForIntegration';
/** `GET /ehr/onboarding/cliniko` — a mutation key despite the `GET_` prefix. */
export const GET_CLINIKO_INFO = 'getClinikoPractitionerInfo';
/** `POST /ehr-launch/mobile/avr/haiku/{id}/push-note`. */
export const PUSH_NOTE_TO_HAIKU_MUTATION_KEY = 'pushNoteToHaiku';
/** `GET /integrations/session/{id}/cerner/note-types`. */
export const GET_CERNER_NOTE_TYPES = 'GetCernerNoteTypes';
/** `GET /integrations/session/{id}/athena/note-push-readiness`. */
const GET_ATHENA_NOTE_PUSH_READINESS = 'GetAthenaNotePushReadiness';
/** `GET /ehr-launch/mobile/avr/haiku/{id}/latest-finalized-recording`. */
export const GET_HAIKU_LATEST_RECORDING_ID = 'GetHaikuLatestRecordingId';
/** `POST /integrations/session/{id}/notes-with-option`. */
export const SYNC_NOTE_WITH_OPTIONS_TO_INTEGRATION_MUTATION_KEY =
  'sync-note-with-options-to-integration';
/**
 * `DELETE /ehr-launch/onboarding/user-ehr-launch-profile/{profileName}`.
 *
 * The constant that held this literal in the app hook was named
 * `DELETE_INTEGRATION` — shorter than, and a poor match for, the value. The
 * VALUE is what a cache identity is, so the value is transcribed and the name
 * is the endpoint's; the old name was module-private and imported by nobody.
 */
export const DELETE_INTEGRATION_EHR_LAUNCH_MUTATION_KEY = 'DeleteIntegrationEhrLaunch';
/**
 * `GET /ehr-launch/onboarding/user-ehr-launch-profile{,/v2}` — the user's EHR
 * launch profiles.
 *
 * MOVED HERE AHEAD OF ITS READ HOOK (ARCFE-1098), which is unusual and is the
 * reason for this note. `useQueryUserEHRLaunchProfiles` is store-tethered
 * (`react-redux` for the auth token and provider) and could not carve; but
 * `useMutateDeleteIntegrationEhrLaunch` — which invalidates this identity and
 * is otherwise headless — could. A package hook may not import
 * `@/hooks/query/**`, so the identity had to single-site here for the write to
 * move at all. The app hook now imports it from this door and RE-EXPORTS it
 * name-intact, so the two other app writers that name it
 * (`useMutateConnectIntegrationEhrLaunch`, `useMutateUpdateIntegrationEhrLaunch`)
 * resolve unchanged.
 *
 * THE FULL READ TUPLE STAYS APP-SIDE and is deliberately not modelled here:
 * it is `[GET_USER_EHR_LAUNCH_PROFILES, { authProvider }]`, and `authProvider`
 * is read from the Rematch store. A factory in this file taking an app enum it
 * cannot name would be a worse lie than the honest partial identity below.
 */
export const GET_USER_EHR_LAUNCH_PROFILES = 'GetUserEHRLaunchProfiles';

// ---------------------------------------------------------------------------
// D3: the four identities that came in with the section-mapping pair, the
// consult-note link read and the sync-document-options read.
//
// All four VALUES are transcribed from the app hook that declared them, and two
// of them are worth reading twice because the value and the name disagree in
// opposite directions:
//
//   - `GET_TEMPLATE_SECTION_MAPPING` is SCREAMING_SNAKE as a value where every
//     other read head on this door is UpperCamel (`GetCernerNoteTypes`,
//     `GetSyncDocumentOptions`). It is the app spelling and it stays.
//   - `REPLACE_TEMPLATE_SECTION_MAPPING_MUTATION_KEY` is the second constant on
//     this door whose BINDING was renamed on the way in while its value was
//     not — the app constant was `REPLACE_TEMPLATE_SECTION_MAPPING` and was
//     module-private, so nothing imported the old name. The `_MUTATION_KEY`
//     suffix follows the neighbours; the literal is the identity.
// ---------------------------------------------------------------------------

/** `GET /integrations/template-section-mappings/{templateId}`. */
export const GET_TEMPLATE_SECTION_MAPPING = 'GET_TEMPLATE_SECTION_MAPPING';
/** `POST /integrations/template-section-mappings`. */
export const REPLACE_TEMPLATE_SECTION_MAPPING_MUTATION_KEY =
  'REPLACE_TEMPLATE_SECTION_MAPPING';
/** `GET /integrations/links/consult-note-link?consult_note_id=`. */
export const GET_INTEGRATION_LINKED_CONSULT_NOTE_INFO =
  'GetIntegrationLinkedConsultNoteInfo';
/** `GET /integrations/documents/{documentId}/sync-options`. */
export const GET_SYNC_DOCUMENT_OPTIONS = 'GetSyncDocumentOptions';
/** `GET /integrations/session/{sessionId}/file/options`. Backend endpoint not shipped yet. */
export const GET_UPLOAD_FILE_OPTIONS = 'GetUploadFileOptions';

// ---------------------------------------------------------------------------
// D4: the seven identities that came in with the six expired-reason hooks —
// the three EHR-launch past-note reads, the two section catalogues, and the
// launch-profile update and Mindbody site-access writes.
//
// THREE OF THE SEVEN CARRY THE EHR-LAUNCH ACCESS TOKEN IN THE KEY, and that is
// transcribed rather than corrected. A credential in a cache identity is not a
// shape anyone would choose, but it is the shape the entries were written
// under: dropping the slot would orphan every cached page, and — the part that
// is not merely cosmetic — it would let a note fetched under one launch's token
// be served to the next launch, which is the behaviour the slot prevents. The
// token is never persisted: `reactQueryPersistConfig` does not dehydrate these
// queries, and the atom behind it is cleared on launch teardown.
//
// TWO WERE MODULE-PRIVATE AT THEIR OLD PATHS and are exported here for the same
// reason the earlier two were — a `/contracts` key factory is the sanctioned
// single declaration site. `AVAILABLE_STANDARDIZED_SECTIONS` was a bare `const`
// and `UPDATE_INTEGRATION` was a bare `const` whose name did not match its
// value; the VALUE is the identity, so the value is transcribed and the binding
// takes the endpoint's name. Neither old name was imported by anything.
// ---------------------------------------------------------------------------

/** `GET /ehr-launch/sessions/{id}/consult-note/past-ehr-notes`. */
export const GET_EHR_LAUNCH_PAST_NOTES = 'GetEhrLaunchPastNotes';
/** `GET /ehr-launch/sessions/{id}/consult-note/commonly-used-past-ehr-notes`. */
export const GET_EHR_LAUNCH_COMMONLY_USED_NOTES = 'GetEhrLaunchCommonlyUsedNotes';
/** `GET /ehr-launch/sessions/{id}/consult-note/past-ehr-note/binary-details`. */
export const GET_EHR_LAUNCH_PAST_NOTE_DETAILS = 'GetEhrLaunchPastNoteDetails';
/** `GET /ehr-launch/mobile/avr/haiku/{id}/available-sections`. */
export const GET_HAIKU_AVAILABLE_SECTIONS = 'GetHaikuAvailableSections';
/**
 * `GET /integrations/standardized-sections` — V2 and V2_1 alike.
 *
 * SCREAMING_SNAKE as a VALUE, like `GET_TEMPLATE_SECTION_MAPPING` and unlike
 * every other read head here. It was a module-private `const` whose name and
 * value coincided, which is the case where a silent normalisation is hardest to
 * spot in review, so it is spelled out rather than tidied.
 */
export const AVAILABLE_STANDARDIZED_SECTIONS = 'AVAILABLE_STANDARDIZED_SECTIONS';
/**
 * `PATCH /ehr-launch/onboarding/user-ehr-launch-profile/{provider}`.
 *
 * The constant that held this literal was named `UPDATE_INTEGRATION` — shorter
 * than, and a poor match for, the value. Same call as
 * `DELETE_INTEGRATION_EHR_LAUNCH_MUTATION_KEY` above: the value is transcribed,
 * the binding takes the endpoint's name, and the old name was module-private.
 */
export const UPDATE_INTEGRATION_EHR_LAUNCH_MUTATION_KEY = 'UpdateIntegrationEhrLaunch';
/**
 * `GET /ehr/onboarding/mindbody-site-access`.
 *
 * kebab-case, like the writeback key and unlike its neighbours. Transcribed.
 */
export const CHECK_MINDBODY_SITE_ACCESS = 'mindbody-site-access';

// ---------------------------------------------------------------------------
// D5: the five write identities that came in with the four writeback/launch
// hooks — three from the sync-notes trio, one from the SDE push, one from the
// Haiku launch-info handshake.
//
// FOUR OF THE FIVE ARE UNREMARKABLE UpperCamel VALUES transcribed from the
// module-private consts that held them, so no old name is owed a re-export.
// THE FIFTH IS NOT, and it is the one to read twice:
// `GENERATE_HAIKU_LAUNCH_INFO_MUTATION_KEY` has the value
// `'useMutateGenerateHaikuLaunchInfo'` — the HOOK'S OWN NAME, not the endpoint's
// and not a resource. Renaming it to match its neighbours would be the tidy
// thing and would also be a cache flush plus a silent break of any
// `useIsMutating` matcher, so the value is transcribed exactly and the oddity is
// recorded here instead.
//
// THREE OF THE FOUR SYNC KEYS SHARE A `{ sessionId }` SLOT AND DIFFER ONLY IN
// THE HEAD, which is load-bearing rather than incidental: `syncNotesToEHR`,
// `syncSectionsNoteToEHR` and `syncNotesToEHRWithEHRLaunch` are three different
// requests over the same session, two of them over the same ROUTE
// (`POST /ehr/uploader/{id}/notes`). Collapsing any pair would make one push's
// `isPending` light up the other's button.
// ---------------------------------------------------------------------------

/** `POST /ehr/uploader/{sessionId}/notes` — the whole-note push. */
export const SYNC_NOTES_TO_EHR = 'SyncNotesToEHR';
/** `POST /ehr-launch/sessions/{sessionId}/consult-note` — the EHR-launch push. */
export const SYNC_NOTES_TO_EHR_WITH_EHR_LAUNCH = 'SyncNotesToEHRWithEHRLaunch';
/**
 * `POST /ehr/uploader/{sessionId}/notes` — the SECTIONAL push, same route as
 * `SYNC_NOTES_TO_EHR` and a different request.
 */
export const SYNC_NOTES_SECTIONS_TO_EHR = 'SyncNotesSectionsToEHR';
/** `POST /ehr-launch/sessions/{sessionId}/consult-note/sectional`. */
export const SYNC_SDES_TO_EHR = 'SyncSDEsToEHR';
/**
 * `POST /ehr-launch/mobile/avr/haiku/launch-info`.
 *
 * THE VALUE IS THE HOOK NAME (`'useMutateGenerateHaikuLaunchInfo'`), which is
 * what the pre-carve module-private const held. Transcribed, not corrected —
 * see the section header.
 */
export const GENERATE_HAIKU_LAUNCH_INFO_MUTATION_KEY = 'useMutateGenerateHaikuLaunchInfo';

// ---------------------------------------------------------------------------
// D6: the two EHR user-search identities, which arrive together because they
// were ONE identity until the split below, and the family record has carried
// the pair as an open item ever since.
//
// WHAT WAS OPEN, AND WHAT CLOSES HERE. Both hooks — the v1 GUID-scoped search
// over `getEHRUsers` and the v2 clinic-scoped one over `getEHRUsersV2` —
// declared `GET_EHR_USER = 'GetEHRUser'` independently, so two different
// endpoints minted the same cache root and shared an invalidation blast radius.
// The earlier pass split the VALUES. What it could not do was single-site them:
// the standing condition recorded on this door is that the duplicate closes via
// this key factory only when a DECLARING hook becomes movable, and until this
// change neither was. Both are now, so both identities land here and the two
// app paths re-export them name-intact. The dated history is on the family
// record (`scripts/monorepo-migration/families/integrations--ehr.json`).
//
// THE HEADS DISAGREE IN SPELLING WITH THEIR CONSTANTS AND WITH EACH OTHER, and
// that is transcribed like every other value in this file: `GET_EHR_USER` is
// `'GetEHRUser'` (the v1 root, kept because renaming it orphans every cached
// search) and `GET_EHR_USER_BY_CLINIC_ID` is `'GetEHRUserByClinicId'`.
// ---------------------------------------------------------------------------

/**
 * `GET /ehr/users?guid=` — the v1, GUID-scoped user search.
 *
 * The name that was duplicated. Its tail is `{ GUID, firstName, lastName }`,
 * with the GUID slot spelled in SCREAMING case because that is what the hook's
 * prop was called; the tail's key names are the identity as much as the head
 * is, so they travel verbatim.
 */
export const GET_EHR_USER = 'GetEHRUser';
/**
 * `POST /ehr/users/v2` — the v2, clinic-scoped user search.
 *
 * SPLIT FROM `GET_EHR_USER`, and the old spelling is the point.
 * Nothing was broken by the collision — the tuples' second elements differ, so
 * no entry ever collided — but the two shared an invalidation blast radius: a
 * prefix operation on `['GetEHRUser']` written for one endpoint would evict the
 * other's cache with no call site saying so. Measured before the split: no such
 * prefix operation existed, and the single external consumer
 * (`…/magentus/hook.ts`) calls `removeQueries` with the FULL v2 tuple, which is
 * an exact-key operation. So the observable behaviour was unchanged; what
 * changed is that the two resources can no longer be invalidated together by
 * accident.
 */
export const GET_EHR_USER_BY_CLINIC_ID = 'GetEHRUserByClinicId';

// ---------------------------------------------------------------------------
// THE POD-OWN BATCH: the identities that came in with the toast-lane hooks the
// earlier passes filed as "blocked on the COPY" rather than on the store. It
// lands beside the two EHR user-search identities above and after them; the two
// batches are independent and the ordering here is chronological, not a
// grouping.
//
// EVERY ONE OF THESE MOVED WITH ITS DECLARING HOOK AND NOT ONE MOVED AHEAD OF
// IT. That is the rule the batch was measured against, and it is why the batch
// is smaller than the set of identities left app-side: an identity whose hook
// could not carve stayed where it was declared, so that no `/contracts` file
// ends up owning a key for a resource nothing behind the door requests.
//
// THE `CONNECT_INTEGRATION` PAIR IS THE ONE TO READ TWICE, and the finding is
// that it was never one identity. Two app hooks each declared a module-private
// const spelled `CONNECT_INTEGRATION`, which reads like the `GET_EHR_USER`
// defect — and is not, because the VALUES differ:
// `useMutateConnectIntegrationEhrLaunch` held `'ConnectIntegrationEhrLaunch'`
// and `useMutateConnectIntegration` holds `'ConnectIntegration'`. A shared
// BINDING NAME is invisible outside its module; only a shared LITERAL is a
// shared cache identity. So there was no collision to split, no invalidation
// blast radius shared between them, and nothing here is a rename. Only the
// EHR-launch half is below, because only its hook carved.
//
// FIVE OF THE TEN OWE A NAME-INTACT RE-EXPORT AT THEIR OLD PATH BECAUSE THEY
// WERE EXPORTED, AND ONLY TWO OF THE FIVE HAVE AN IMPORTER TODAY. Measured
// repo-wide rather than assumed: `SYNC_SECTIONAL_NOTE_TO_INTEGRATION_MUTATION_KEY`
// is read from the old path by `usePushNoteActions.ts`, which builds a
// `mutationKey` from it, and `GET_EHR_APPOINTMENTS` is read from the old path by
// four production writes (`useMutateDeleteSessionsByIds`,
// `useMutateDeleteAllSessions`, `useMutateUpdateSessionEHRDetails`,
// `useMutateUpdateSessionEHRAppointmentId`), two of which build the full tuple by
// hand. `MUTATE_VALIDATE_PAIRING_CODE`, `MUTATE_PAIR_BP_SITE` and
// `GET_EHR_USER_BY_SITE_ID` have ZERO importers anywhere.
//
// THE THREE WITH NO IMPORTER KEEP THEIR RE-EXPORT ANYWAY, and the reason is that
// the rule is about the CONVENTION rather than about today's fan-in: an exported
// identity keeps its old specifier resolving, so a consumer written against the
// old path in a branch that predates this one still compiles after the merge.
// Applying the rule only where a grep finds a caller makes compatibility a
// function of merge order. What that costs is three exported bindings nothing
// reads yet; what it buys is that "was it exported before" is the whole test.
// ---------------------------------------------------------------------------

/** `POST /integrations/session/{sessionId}/file` — the AlayaCare file upload. */
export const UPLOAD_FILE_TO_INTEGRATION_MUTATION_KEY = 'upload-file-to-integration';
/**
 * `GET /ehr/onboarding/pairing-code` — a mutation key over a GET, like
 * `GET_CLINIKO_INFO` and `CHECK_MINDBODY_SITE_ACCESS` above.
 *
 * SCREAMING_SNAKE as a VALUE, and the name and the value coincide — the third
 * such case on this door after `GET_TEMPLATE_SECTION_MAPPING` and
 * `AVAILABLE_STANDARDIZED_SECTIONS`, and the case where a silent normalisation
 * is hardest to spot in review.
 */
export const MUTATE_VALIDATE_PAIRING_CODE = 'MUTATE_VALIDATE_PAIRING_CODE';
/**
 * `GET /ehr/onboarding/bp-pair-site` — the Best Practice site pairing, another
 * mutation key over a GET, and the second SCREAMING_SNAKE value whose name and
 * value coincide.
 */
export const MUTATE_PAIR_BP_SITE = 'MUTATE_PAIR_BP_SITE';
/**
 * `GET /ehr/users` — the clinic-scoped user-directory search run as a mutation.
 *
 * THE THIRD IDENTITY OVER THIS ROUTE, and the count is the point. `getEHRUsers`
 * and `getEHRUsersByClinicId` both `GET /ehr/users`; the first is read by
 * `GET_EHR_USER` above and the second by this. A fourth,
 * `GET_EHR_USER_BY_CLINIC_ID`, keys the `POST /ehr/users/v2` variant. Four names
 * for one route family looks like drift and is not: each addresses a different
 * request, and the pair that DID share a literal was split rather than hoisted,
 * for the reason `../data/api/users.ts` records.
 */
export const GET_EHR_USER_BY_SITE_ID = 'GET_EHR_USER_BY_SITE_ID';
/** `PATCH /integrations/links/sessions/{sessionId}`. */
export const UPDATE_INTEGRATION_SESSION_LINK = 'UpdateIntegrationSessionLink';
/**
 * `POST /integrations/documents/{documentId}/sync`.
 *
 * kebab-case, and NOT the same identity as the sync-note keys it reads like.
 * `SYNC_NOTE_TO_INTEGRATION_MUTATION_KEY` ('sync-note-to-integration') is still
 * app-side with the desktop-bridged hook that declares it; this is the DOCUMENT
 * push and a different route.
 */
export const SYNC_DOCUMENT_TO_INTEGRATION_MUTATION_KEY = 'sync-document-to-integration';
/**
 * `POST /integrations/session/{sessionId}/sectional-notes`.
 *
 * ONE ROUTE TAIL APART from `syncNoteToIntegration` (`/notes`), whose key
 * `SYNC_NOTE_TO_INTEGRATION_MUTATION_KEY` ('sync-note-to-integration') is still
 * app-side with its desktop-bridged hook. Same session, same connected
 * integration, two different requests — so two identities.
 */
export const SYNC_SECTIONAL_NOTE_TO_INTEGRATION_MUTATION_KEY =
  'sync-sectional-note-to-integration';
/**
 * `POST /ehr-launch/onboarding/user-ehr-launch-profile/v2`.
 *
 * THE HALF OF THE `CONNECT_INTEGRATION` PAIR THAT CARVED, and the binding is
 * renamed on the way in while the VALUE is not — the fourth time that has
 * happened on this door, after the two `..._EHR_LAUNCH_MUTATION_KEY` pair and
 * `REPLACE_TEMPLATE_SECTION_MAPPING_MUTATION_KEY`. The app const was
 * `CONNECT_INTEGRATION`, module-private, imported by nothing.
 *
 * IT WAS NEVER A DUPLICATE IDENTITY, which is the finding rather than a
 * footnote. `useMutateConnectIntegration` declares a module-private const with
 * the SAME NAME and a DIFFERENT VALUE — `'ConnectIntegration'`, over
 * `POST /user/ehr-profile`, an endpoint the `workspace` family owns. A shared
 * binding name is invisible outside its module; only a shared LITERAL is a
 * shared cache identity, so the two never collided and never shared an
 * invalidation blast radius. Nothing here is a rename of a live identity, and
 * the other half stays declared where it is with the hook that could not carve.
 */
export const CONNECT_INTEGRATION_EHR_LAUNCH_MUTATION_KEY = 'ConnectIntegrationEhrLaunch';
/**
 * `GET /ehr/appointments` — the OLD-FRAMEWORK day view.
 *
 * TWO APP WRITES BUILD THIS TUPLE BY HAND from the re-exported head
 * (`useMutateUpdateSessionEHRAppointmentId` and `useMutateUpdateSessionEHRDetails`
 * both `cancelQueries` / `setQueryData` / `invalidateQueries` on
 * `[GET_EHR_APPOINTMENTS, { date }]`), and both stay app-side because their own
 * endpoint is `session`-owned. So the SLOT NAME is frozen by name below, not just
 * the head: renaming `date` here silently stops three optimistic updates from
 * matching this hook's entry.
 */
export const GET_EHR_APPOINTMENTS = 'GetEHRAppointments';
/**
 * `POST /integrations/workflows/session` — creating a Heidi session from an EHR
 * appointment.
 *
 * ITS DECLARING FILE HELD A SECOND IDENTITY THAT DID NOT COME WITH IT, which is
 * the rule rather than an oversight: `GET_INTEGRATION_LINKED_SESSION_INFO` is an
 * ad-hoc `fetchQuery` head minted inside that hook's 409 recovery, which runs on
 * the app's own query-client singleton and stays app-side with it. It is also a
 * recorded live defect — it reads `'GetIntegrationLinkedSessionInfo'` where the
 * read hook writes `'GetIntegrationLinkSessionInfo'`, one letter apart — so
 * moving it here would freeze the defect behind a door.
 */
export const POST_INTEGRATION_WORKFLOW_SESSION_MUTATION_KEY =
  'PostIntegrationWorkflowSession';

/**
 * `GET /template-gallery/integrations-marketplace/integrations/integration-providers?country=`
 * returns the country-scoped EHR provider list. The first identity on this
 * door minted IN /contracts rather than transcribed from an app hook.
 */
export const GET_INTEGRATION_PROVIDERS = 'GetIntegrationProviders';
/**
 * `POST /integrations/filtered-schedule` — a READ over POST (the body keeps
 * filter values out of access-log query strings), so it is a query key, not a
 * mutation key. Minted in /contracts; no app hook ever declared it.
 */
export const GET_FILTERED_SCHEDULE = 'GetFilteredSchedule';

/** TanStack query keys for the `./ehr` door's carved read hooks. */
export const ehrQueryKeys = {
  // Bare `sessionId`, NOT `{ sessionId }` — unlike its two neighbours below.
  syncNoteOptions: (sessionId: string) => [GET_SYNC_NOTE_OPTIONS, sessionId] as const,
  athenaNotePushReadiness: (sessionId: string | undefined) =>
    [GET_ATHENA_NOTE_PUSH_READINESS, { sessionId }] as const,
  cernerNoteTypes: (sessionId: string | undefined) =>
    [GET_CERNER_NOTE_TYPES, { sessionId }] as const,
  haikuLatestRecordingId: (externalSessionId: string | undefined) =>
    [GET_HAIKU_LATEST_RECORDING_ID, { externalSessionId }] as const,
  /**
   * The INVALIDATION PREFIX for the user's EHR launch profiles, not the read
   * tuple — see `GET_USER_EHR_LAUNCH_PROFILES` for why the read tuple cannot
   * live here. `[head]` is exactly what the three app writers pass to
   * `invalidateQueries`, and a prefix match is what makes it reach the read's
   * `[head, { authProvider }]` entry whatever the provider is.
   */
  userEhrLaunchProfilesPrefix: () => [GET_USER_EHR_LAUNCH_PROFILES] as const,
  /**
   * `{ date }` — the date-only string, and `undefined` is a real cached key
   * because the hook builds the tuple before a date exists and gates the fetch
   * with `skipToken`.
   */
  ehrAppointments: (date: string | undefined) =>
    [GET_EHR_APPOINTMENTS, { date }] as const,
  integrationProviders: (country: string | undefined) =>
    [GET_INTEGRATION_PROVIDERS, { country }] as const,
  /**
   * The whole request body is the identity: the same filters pinned to a
   * different `onboardingSessionId`, or continued from a different `cursor`,
   * is a different page of a different search. `filters` is a plain object
   * of backend-owned filter keys, which React Query hashes structurally.
   */
  filteredSchedule: (params: {
    filters: Record<string, string>;
    onboardingSessionId: string | null | undefined;
    pageSize: number | null | undefined;
    cursor: string | null | undefined;
  }) =>
    [
      GET_FILTERED_SCHEDULE,
      {
        filters: params.filters,
        onboardingSessionId: params.onboardingSessionId,
        pageSize: params.pageSize,
        cursor: params.cursor,
      },
    ] as const,
  templateSectionMapping: (templateId: string | undefined) =>
    [GET_TEMPLATE_SECTION_MAPPING, { templateId }] as const,
  integrationLinkedConsultNoteInfo: (consultNoteId: string | undefined) =>
    [GET_INTEGRATION_LINKED_CONSULT_NOTE_INFO, { consultNoteId }] as const,
  /**
   * THREE SLOTS IN ONE OBJECT, AND THE ORDER MATTERS LESS THAN THE PRESENCE.
   * `integrationEhrProvider` and `integrationOnboardingSessionId` are resolved
   * app-side from `useIntegrationEhrInfo` and passed in; they are part of the
   * identity because the same document has different sync options under
   * different connected integrations. Dropping either slot would serve one
   * provider's options from another's cache entry.
   */
  syncDocumentOptions: (params: {
    documentId: string | undefined;
    integrationEhrProvider: string | undefined;
    integrationOnboardingSessionId: string | undefined;
  }) =>
    [
      GET_SYNC_DOCUMENT_OPTIONS,
      {
        documentId: params.documentId,
        integrationEhrProvider: params.integrationEhrProvider,
        integrationOnboardingSessionId: params.integrationOnboardingSessionId,
      },
    ] as const,
  /**
   * No provider slot, unlike `syncDocumentOptions`. The upload-file route is
   * session-scoped (`/session/{sessionId}/file`), and a session resolves to
   * at most one connected integration, so `sessionId` already is the
   * provider-scoping identity here.
   */
  uploadFileOptions: (params: { sessionId: string; documentId: string | undefined }) =>
    [
      GET_UPLOAD_FILE_OPTIONS,
      { sessionId: params.sessionId, documentId: params.documentId },
    ] as const,
  /**
   * THREE ELEMENTS, WITH THE LITERAL `'infinite'` IN THE MIDDLE. This is the
   * only key on this door with a discriminator slot, and it is not decoration:
   * the hook is a `useInfiniteQuery`, whose cached value is a
   * `{ pages, pageParams }` envelope rather than a page. Dropping the slot
   * would let a plain `useQuery` over the same resource collide with it and
   * read `data.pages` off a bare response.
   */
  ehrLaunchPastNotes: (params: {
    sessionId: string;
    accessToken: string | null | undefined;
  }) =>
    [
      GET_EHR_LAUNCH_PAST_NOTES,
      'infinite',
      { sessionId: params.sessionId, accessToken: params.accessToken },
    ] as const,
  ehrLaunchCommonlyUsedNotes: (params: {
    sessionId: string;
    accessToken: string | null | undefined;
  }) =>
    [
      GET_EHR_LAUNCH_COMMONLY_USED_NOTES,
      { sessionId: params.sessionId, accessToken: params.accessToken },
    ] as const,
  /**
   * SLOT ORDER IS `sessionId, resourceId, accessToken` and it is load-bearing
   * in a way the sibling keys' are not: `useHandlePastEhrNoteToggle` builds
   * this tuple BY HAND from the re-exported head constant and hands it to
   * `queryClient.fetchQuery`, so a reordered or renamed slot there stops
   * matching this hook's entry and silently re-fetches every toggle.
   */
  ehrLaunchPastNoteDetails: (params: {
    sessionId: string;
    resourceId: string;
    accessToken: string | null | undefined;
  }) =>
    [
      GET_EHR_LAUNCH_PAST_NOTE_DETAILS,
      {
        sessionId: params.sessionId,
        resourceId: params.resourceId,
        accessToken: params.accessToken,
      },
    ] as const,
  haikuAvailableSections: (haikuSessionId: string | undefined) =>
    [GET_HAIKU_AVAILABLE_SECTIONS, { haikuSessionId }] as const,
  /**
   * THE CATALOGUE FLAG IS PART OF THE IDENTITY, not just of the transport
   * branch. V2 returns section strings and V2_1 returns section objects for the
   * SAME onboarding session, so one entry for both would serve a V2 shape to a
   * V2_1 caller — a render crash rather than a stale list.
   */
  availableStandardizedSections: (params: {
    integrationOnboardingSessionId: string | null | undefined;
    isAmdMarkdownSectionsCatalogue: boolean;
  }) =>
    [
      AVAILABLE_STANDARDIZED_SECTIONS,
      {
        integrationOnboardingSessionId: params.integrationOnboardingSessionId,
        isAmdMarkdownSectionsCatalogue: params.isAmdMarkdownSectionsCatalogue,
      },
    ] as const,
  /**
   * THE `GUID` SLOT IS SCREAMING-CASE AND THE OTHER TWO ARE NOT. Transcribed
   * from the hook that shipped it: `[GET_EHR_USER, { GUID, firstName, lastName }]`.
   * A tail key name is part of the identity — TanStack hashes the object — so
   * lowercasing it here would orphan every cached search rather than tidy one.
   *
   * `firstName` and `lastName` are optional and the slots are present either
   * way: the hook builds this key before the form is complete and gates the
   * fetch with `skipToken`, so the undefined-bearing tuple is a real entry.
   */
  ehrUser: (params: {
    GUID: string;
    firstName: string | undefined;
    lastName: string | undefined;
  }) =>
    [
      GET_EHR_USER,
      {
        GUID: params.GUID,
        firstName: params.firstName,
        lastName: params.lastName,
      },
    ] as const,
  /**
   * FOUR SLOTS, AND `ehrProvider` IS THE ONE THAT KEEPS THIS TUPLE OUT OF THE
   * v1's PREFIX. The two searches are different resources over different
   * endpoints; the heads were split apart and the tails never matched.
   */
  ehrUserByClinicId: (params: {
    ehrProvider: string;
    clinicId: string;
    firstName: string;
    lastName: string;
  }) =>
    [
      GET_EHR_USER_BY_CLINIC_ID,
      {
        ehrProvider: params.ehrProvider,
        clinicId: params.clinicId,
        firstName: params.firstName,
        lastName: params.lastName,
      },
    ] as const,
};

/** TanStack mutation keys for the `./ehr` door's carved write hooks. */
export const ehrMutationKeys = {
  clinikoPractitionerInfo: () => [GET_CLINIKO_INFO] as const,
  pushNoteToHaiku: () => [PUSH_NOTE_TO_HAIKU_MUTATION_KEY] as const,
  syncNoteWithOptions: () =>
    [SYNC_NOTE_WITH_OPTIONS_TO_INTEGRATION_MUTATION_KEY] as const,
  deleteIntegrationEhrLaunch: () => [DELETE_INTEGRATION_EHR_LAUNCH_MUTATION_KEY] as const,
  /**
   * A BARE `templateId` in slot 2, not `{ templateId }` — unlike the READ key
   * for the same resource, three lines up. Transcribed from the app hook, which
   * wrote `mutationKey: [REPLACE_TEMPLATE_SECTION_MAPPING, templateId]` while
   * writing `[GET_TEMPLATE_SECTION_MAPPING, { templateId }]` for the query it
   * optimistically updates. Normalising the pair would look like tidying and
   * would break `useIsMutating` for every caller that already matches on it.
   */
  replaceTemplateSectionMapping: (templateId: string | undefined) =>
    [REPLACE_TEMPLATE_SECTION_MAPPING_MUTATION_KEY, templateId] as const,
  updateIntegrationEhrLaunch: () => [UPDATE_INTEGRATION_EHR_LAUNCH_MUTATION_KEY] as const,
  retrieveMindbodySiteAccess: () => [CHECK_MINDBODY_SITE_ACCESS] as const,
  syncNotesToEhr: (sessionId: string) => [SYNC_NOTES_TO_EHR, { sessionId }] as const,
  syncNotesToEhrWithEhrLaunch: (sessionId: string) =>
    [SYNC_NOTES_TO_EHR_WITH_EHR_LAUNCH, { sessionId }] as const,
  syncSectionsNoteToEhr: (sessionId: string) =>
    [SYNC_NOTES_SECTIONS_TO_EHR, { sessionId }] as const,
  syncSdesToEhr: (sessionId: string) => [SYNC_SDES_TO_EHR, { sessionId }] as const,
  /**
   * ONE ELEMENT, NO SESSION SLOT. The Haiku launch handshake runs before any
   * Heidi session exists — it is the call that mints one — so there is nothing
   * to key on and the single-element tuple is correct rather than lazy.
   */
  generateHaikuLaunchInfo: () => [GENERATE_HAIKU_LAUNCH_INFO_MUTATION_KEY] as const,
  uploadFileToIntegration: (sessionId: string) =>
    [UPLOAD_FILE_TO_INTEGRATION_MUTATION_KEY, { sessionId }] as const,
  validatePairingCode: () => [MUTATE_VALIDATE_PAIRING_CODE] as const,
  pairBpSite: () => [MUTATE_PAIR_BP_SITE] as const,
  getEhrUserBySiteId: () => [GET_EHR_USER_BY_SITE_ID] as const,
  /**
   * `{ sessionId }` WRAPPED, like the file upload and unlike the sibling READ of
   * the same resource — `useQueryIntegrationLinkSessionInfo` keys
   * `[GET_INTEGRATION_LINK_SESSION_INFO, { sessionId }]`, which happens to agree.
   * Transcribed either way: what makes the wrap load-bearing here is that two
   * sessions' link updates must not share an `isPending`.
   */
  updateIntegrationSessionLink: (sessionId: string) =>
    [UPDATE_INTEGRATION_SESSION_LINK, { sessionId }] as const,
  /**
   * ONE ELEMENT, NO DOCUMENT SLOT — unlike the READ of the same resource, which
   * keys on three. Transcribed: the pre-carve hook took its `documentId` as a
   * mutation VARIABLE rather than a hook argument, so every document's push
   * shares one `isPending`. Adding the slot would be an improvement and would
   * also change which button spins.
   */
  syncDocumentToIntegration: () => [SYNC_DOCUMENT_TO_INTEGRATION_MUTATION_KEY] as const,
  /**
   * ONE ELEMENT, NO SESSION SLOT — unlike the four `{ sessionId }` write keys
   * above. Transcribed: the pre-carve hook took `sessionId` as a mutation
   * VARIABLE, so all sessions' sectional pushes share one `isPending`.
   */
  syncSectionalNoteToIntegration: () =>
    [SYNC_SECTIONAL_NOTE_TO_INTEGRATION_MUTATION_KEY] as const,
  connectIntegrationEhrLaunch: () =>
    [CONNECT_INTEGRATION_EHR_LAUNCH_MUTATION_KEY] as const,
  postIntegrationWorkflowSession: () =>
    [POST_INTEGRATION_WORKFLOW_SESSION_MUTATION_KEY] as const,
};
